import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Gavel, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

export default function BanAppeal() {
  const { guildId } = useParams();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/ban-appeals/${guildId}`, { reason }, authHeader());
      setResult('success');
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to submit appeal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-2xl p-6"
        data-testid="ban-appeal-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
            <Gavel size={18} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-white font-semibold font-[Outfit]">Ban Appeal</h1>
            <p className="text-zinc-500 text-xs font-[Manrope]">Submit a request to be unbanned</p>
          </div>
        </div>

        {result === 'success' ? (
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <CheckCircle size={36} className="text-emerald-400" />
            <p className="text-white font-medium font-[Manrope]">Appeal submitted</p>
            <p className="text-zinc-500 text-sm font-[Manrope]">A moderator will review your appeal soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <textarea
              data-testid="appeal-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you should be unbanned..."
              rows={6}
              maxLength={1000}
              className="w-full bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#5865F2] font-[Manrope] resize-none"
            />
            {error && <p className="text-red-400 text-xs font-[Manrope]">{error}</p>}
            <button
              type="submit"
              data-testid="appeal-submit-btn"
              disabled={submitting || !reason.trim()}
              className="w-full py-2.5 rounded-xl bg-[#5865F2] text-white text-sm font-medium font-[Manrope] hover:bg-[#4752C4] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Appeal'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
